import {Prequal} from '../../../Core/mobx/ParameterStore/Prequal';
import {productScriptPrimary, productScriptFallback, getProductFallbackTriggers} from '../productConstants';

interface ProductEligibility {
    iulProducts: any[];
    termProducts: any[];
    vulProducts: any[];
    finalExpenseProducts: any[];
    annuityProducts: any[];
    adbProducts: any[];
}

// Helper to check if there are eligible products in a category
const isThereEligibleProduct = (products: any[]): boolean => {
    return products && products.length > 0;
};

// Determine product type recommendations based on user inputs
export const determineProductTypeRecommendations = (prequal: Prequal, productEligibility: ProductEligibility) => {
    const {iulProducts, termProducts, vulProducts, finalExpenseProducts, annuityProducts, adbProducts} =
        productEligibility;

    const eligibleIulProduct = isThereEligibleProduct(iulProducts);
    const eligibleTermProduct = isThereEligibleProduct(termProducts);
    const eligibleVulProduct = isThereEligibleProduct(vulProducts);
    const eligibleFinalExpenseProduct = isThereEligibleProduct(finalExpenseProducts);
    const eligibleAnnuityProduct = isThereEligibleProduct(annuityProducts);
    const eligibleAdbProduct = isThereEligibleProduct(adbProducts);

    const recommendationEligible = (rec: string) => {
        switch (rec) {
            case 'IUL':
                return eligibleIulProduct;
            case 'Term':
                return eligibleTermProduct;
            case 'VUL':
                return eligibleVulProduct;
            case 'Whole Life':
                return eligibleFinalExpenseProduct;
            case 'Annuity':
                return eligibleAnnuityProduct;
            case 'ADB':
                return eligibleAdbProduct;
            default:
                return false;
        }
    };

    const setRecommendationsHelper = (
        recOne: string,
        recTwo: string,
        fallbackTriggers: string,
        setFallbackTriggerRegardless: boolean = false,
    ): boolean => {
        if (recommendationEligible(recOne) && recommendationEligible(recTwo)) {
            prequal.recommendedProductPrimary.setValue(recOne);
            prequal.recommendedProductFallback.setValue(recTwo);
            prequal.recommendedProductFallbackTriggers.setValue(getProductFallbackTriggers(recTwo));
            prequal.recommendedProductPrimaryScript.setValue(productScriptPrimary(recOne));
            prequal.recommendedProductFallbackScript.setValue(productScriptFallback(recTwo));
            return true;
        }
        if (recommendationEligible(recOne)) {
            prequal.recommendedProductPrimary.setValue(recOne);
            prequal.recommendedProductPrimaryScript.setValue(productScriptPrimary(recOne));
            if (setFallbackTriggerRegardless) {
                prequal.recommendedProductFallbackTriggers.setValue(fallbackTriggers);
            }
            return true;
        }
        if (recommendationEligible(recTwo)) {
            prequal.recommendedProductPrimary.setValue(recTwo);
            prequal.recommendedProductPrimaryScript.setValue(productScriptPrimary(recTwo));
            if (setFallbackTriggerRegardless) {
                prequal.recommendedProductFallbackTriggers.setValue(fallbackTriggers);
            }
            return true;
        }
        return false;
    };

    const resetRecommendations = () => {
        prequal.recommendedProductPrimary.setValue('');
        prequal.recommendedProductPrimaryScript.setValue('');
        prequal.recommendedProductFallback.setValue('');
        prequal.recommendedProductFallbackTriggers.setValue('');
        prequal.recommendedProductFallbackScript.setValue('');
        prequal.recommendedProductIneligibleForOriginalRecommendations.setValue('');
    };

    const setRecommendations = (recOne: string, recTwo: string, fallbackTriggers: string = '') => {
        // Determine what the actual recommendations will be (including fallbacks)
        let actualPrimary = '';
        let actualFallback = '';
        let willBeIneligible = false;

        if (recommendationEligible(recOne) && recommendationEligible(recTwo)) {
            actualPrimary = recOne;
            actualFallback = recTwo;
        } else if (recommendationEligible(recOne)) {
            actualPrimary = recOne;
        } else if (recommendationEligible(recTwo)) {
            actualPrimary = recTwo;
        } else {
            willBeIneligible = true;
            // Fallback logic based on focus
            if (['PROTECTION', 'INHERITANCE'].includes(prequal.intentFocus.value)) {
                if (recommendationEligible('ADB')) {
                    actualPrimary = 'ADB';
                    if (recommendationEligible('Annuity')) {
                        actualFallback = 'Annuity';
                    }
                } else if (recommendationEligible('Annuity')) {
                    actualPrimary = 'Annuity';
                }
            } else if (['RETIREMENT', 'SAVINGS'].includes(prequal.intentFocus.value)) {
                if (recommendationEligible('Annuity')) {
                    actualPrimary = 'Annuity';
                    if (recommendationEligible('ADB')) {
                        actualFallback = 'ADB';
                    }
                } else if (recommendationEligible('ADB')) {
                    actualPrimary = 'ADB';
                }
            }
        }

        // Check if we're already set to these actual recommendations to avoid infinite loop
        if (
            prequal.recommendedProductPrimary.value === actualPrimary &&
            prequal.recommendedProductFallback.value === actualFallback &&
            prequal.recommendedProductIneligibleForOriginalRecommendations.value === (willBeIneligible ? 'true' : '')
        ) {
            return;
        }

        // reset all fields
        resetRecommendations();

        if (!setRecommendationsHelper(recOne, recTwo, fallbackTriggers)) {
            // this means neither recommendations were eligible
            prequal.recommendedProductIneligibleForOriginalRecommendations.setValue('true');
            if (['PROTECTION', 'INHERITANCE'].includes(prequal.intentFocus.value)) {
                setRecommendationsHelper(
                    'ADB',
                    'Annuity',
                    'Note: The customer was ineligible for ' + recOne + ' and ' + recTwo,
                    true,
                );
            } else if (['RETIREMENT', 'SAVINGS'].includes(prequal.intentFocus.value)) {
                setRecommendationsHelper(
                    'Annuity',
                    'ADB',
                    'Note: The customer was ineligible for ' + recOne + ' and ' + recTwo,
                    true,
                );
            }
        }
    };

    // Calculate household income category
    var householdIncomeCategory = 'HHI_LTE35';
    if (prequal.incomeHouseholdText.value && !isNaN(prequal.incomeHouseholdText.value as any)) {
        const hhi = Number(prequal.incomeHouseholdText.value);
        if (hhi > 35000) {
            householdIncomeCategory = 'HHI_GT35';
        }
    }

    // Calculate age category
    var ageCategory = '';
    const age = prequal.age.value as number;
    if (age >= 20 && age <= 50) {
        ageCategory = 'AGE_20_50';
    } else if (age > 50) {
        ageCategory = 'AGE_50_PLUS';
    }

    const coverageAmount =
        prequal.recommendedCoverageAmount.value && !isNaN(prequal.recommendedCoverageAmount.value as any)
            ? Number(prequal.recommendedCoverageAmount.value)
            : 0;

    const evaluateRules = ({
        focusCategories,
        age,
        income,
        coverage,
        fundingPlan,
    }: {
        focusCategories: string[];
        age: string;
        income: string;
        coverage?: boolean;
        fundingPlan?: string[];
    }) => {
        if (
            focusCategories.includes(prequal.intentFocus.value as string) &&
            ageCategory === age &&
            householdIncomeCategory === income
        ) {
            if (coverage !== undefined && !coverage) {
                return false;
            }
            if (fundingPlan !== undefined && !fundingPlan.includes(prequal.intentFunding.value as string)) {
                return false;
            }
            return true;
        }
        return false;
    };

    // Apply recommendation rules based on user inputs
    // These rules determine the primary and fallback product types

    // Check if no products are eligible
    const anyEligibleProducts =
        eligibleIulProduct ||
        eligibleVulProduct ||
        eligibleTermProduct ||
        eligibleFinalExpenseProduct ||
        eligibleAnnuityProduct ||
        eligibleAdbProduct;

    if (!anyEligibleProducts) {
        resetRecommendations();
        prequal.recommendedProductFallbackTriggers.setValue('No product eligible or recommendations still loading.');
        return;
    }

    // Rule evaluations from spreadsheet: https://docs.google.com/spreadsheets/d/1Y0J8lw8DGU59By6YRpT6TL_17mFcdxdydf55s_sp5RA/edit?gid=314196124#gid=314196124
    // row 2
    if (evaluateRules({focusCategories: ['PROTECTION'], age: 'AGE_20_50', income: 'HHI_LTE35'})) {
        setRecommendations('Term', 'ADB');
        return;
    }
    // row 3
    if (evaluateRules({focusCategories: ['PROTECTION'], age: 'AGE_20_50', income: 'HHI_GT35'})) {
        setRecommendations('IUL', 'Term');
        return;
    }
    // row 4
    if (evaluateRules({focusCategories: ['PROTECTION'], age: 'AGE_50_PLUS', income: 'HHI_LTE35'})) {
        setRecommendations('Term', 'Whole Life');
        return;
    }
    // row 5
    if (
        evaluateRules({
            focusCategories: ['PROTECTION'],
            age: 'AGE_50_PLUS',
            income: 'HHI_GT35',
            coverage: coverageAmount < 100000,
        })
    ) {
        setRecommendations('IUL', 'Whole Life');
        return;
    }
    // row 6
    if (
        evaluateRules({
            focusCategories: ['PROTECTION'],
            age: 'AGE_50_PLUS',
            income: 'HHI_GT35',
            coverage: coverageAmount >= 100000,
        })
    ) {
        setRecommendations('IUL', 'Term');
        return;
    }
    // row 7
    if (
        evaluateRules({
            focusCategories: ['RETIREMENT'],
            age: 'AGE_20_50',
            income: 'HHI_LTE35',
            fundingPlan: ['FUND_LUMP'],
        })
    ) {
        setRecommendations('Annuity', 'IUL');
        return;
    }
    // row 8
    if (
        evaluateRules({
            focusCategories: ['RETIREMENT'],
            age: 'AGE_20_50',
            income: 'HHI_GT35',
            fundingPlan: ['FUND_LUMP'],
        })
    ) {
        setRecommendations('IUL', 'Annuity');
        return;
    }
    // row 9
    if (
        evaluateRules({
            focusCategories: ['SAVINGS'],
            age: 'AGE_20_50',
            income: 'HHI_LTE35',
            fundingPlan: ['FUND_LUMP'],
        })
    ) {
        setRecommendations('IUL', 'Annuity');
        return;
    }
    // row 10
    if (
        evaluateRules({
            focusCategories: ['SAVINGS'],
            age: 'AGE_20_50',
            income: 'HHI_GT35',
            fundingPlan: ['FUND_LUMP'],
        })
    ) {
        setRecommendations('IUL', 'Annuity');
        return;
    }
    // row 11
    if (
        evaluateRules({
            focusCategories: ['RETIREMENT'],
            age: 'AGE_20_50',
            income: 'HHI_LTE35',
            fundingPlan: ['FUND_MONTHLY', 'FUND_BOTH'],
        })
    ) {
        setRecommendations('Annuity', 'IUL');
        return;
    }
    // row 12
    if (
        evaluateRules({
            focusCategories: ['RETIREMENT'],
            age: 'AGE_20_50',
            income: 'HHI_GT35',
            fundingPlan: ['FUND_MONTHLY', 'FUND_BOTH'],
        })
    ) {
        setRecommendations('IUL', 'Annuity');
        return;
    }
    // row 13
    if (
        evaluateRules({
            focusCategories: ['SAVINGS'],
            age: 'AGE_20_50',
            income: 'HHI_LTE35',
            fundingPlan: ['FUND_MONTHLY', 'FUND_BOTH'],
        })
    ) {
        setRecommendations('IUL', 'Annuity');
        return;
    }
    // row 14
    if (
        evaluateRules({
            focusCategories: ['SAVINGS'],
            age: 'AGE_20_50',
            income: 'HHI_GT35',
            fundingPlan: ['FUND_MONTHLY', 'FUND_BOTH'],
        })
    ) {
        setRecommendations('IUL', 'Annuity');
        return;
    }
    // row 15
    if (
        evaluateRules({
            focusCategories: ['RETIREMENT'],
            age: 'AGE_50_PLUS',
            income: 'HHI_LTE35',
            fundingPlan: ['FUND_LUMP'],
        })
    ) {
        setRecommendations('Annuity', 'Whole Life');
        return;
    }
    // row 16
    if (
        evaluateRules({
            focusCategories: ['RETIREMENT'],
            age: 'AGE_50_PLUS',
            income: 'HHI_GT35',
            fundingPlan: ['FUND_LUMP'],
        })
    ) {
        setRecommendations('Annuity', 'IUL');
        return;
    }
    // row 17
    if (
        evaluateRules({
            focusCategories: ['SAVINGS'],
            age: 'AGE_50_PLUS',
            income: 'HHI_LTE35',
            fundingPlan: ['FUND_LUMP'],
        })
    ) {
        setRecommendations('Annuity', 'IUL');
        return;
    }
    // row 18
    if (
        evaluateRules({
            focusCategories: ['SAVINGS'],
            age: 'AGE_50_PLUS',
            income: 'HHI_GT35',
            fundingPlan: ['FUND_LUMP'],
        })
    ) {
        setRecommendations('IUL', 'Annuity');
        return;
    }
    // row 19
    if (
        evaluateRules({
            focusCategories: ['RETIREMENT'],
            age: 'AGE_50_PLUS',
            income: 'HHI_LTE35',
            fundingPlan: ['FUND_MONTHLY', 'FUND_BOTH'],
        })
    ) {
        setRecommendations('Annuity', 'IUL');
        return;
    }
    // row 20
    if (
        evaluateRules({
            focusCategories: ['RETIREMENT'],
            age: 'AGE_50_PLUS',
            income: 'HHI_GT35',
            fundingPlan: ['FUND_MONTHLY', 'FUND_BOTH'],
        })
    ) {
        setRecommendations('Annuity', 'IUL');
        return;
    }
    // row 21
    if (
        evaluateRules({
            focusCategories: ['SAVINGS'],
            age: 'AGE_50_PLUS',
            income: 'HHI_LTE35',
            fundingPlan: ['FUND_MONTHLY', 'FUND_BOTH'],
        })
    ) {
        setRecommendations('Annuity', 'IUL');
        return;
    }
    // row 22
    if (
        evaluateRules({
            focusCategories: ['SAVINGS'],
            age: 'AGE_50_PLUS',
            income: 'HHI_GT35',
            fundingPlan: ['FUND_MONTHLY', 'FUND_BOTH'],
        })
    ) {
        setRecommendations('IUL', 'Annuity');
        return;
    }
    // row 23
    if (
        evaluateRules({
            focusCategories: ['INHERITANCE'],
            age: 'AGE_20_50',
            income: 'HHI_LTE35',
            fundingPlan: ['FUND_LUMP'],
        })
    ) {
        setRecommendations('IUL', 'Whole Life');
        return;
    }
    // row 24
    if (
        evaluateRules({
            focusCategories: ['INHERITANCE'],
            age: 'AGE_20_50',
            income: 'HHI_GT35',
            fundingPlan: ['FUND_LUMP'],
        })
    ) {
        setRecommendations('IUL', 'Whole Life');
        return;
    }
    // row 25
    if (
        evaluateRules({
            focusCategories: ['INHERITANCE'],
            age: 'AGE_20_50',
            income: 'HHI_LTE35',
            fundingPlan: ['FUND_MONTHLY', 'FUND_BOTH'],
        })
    ) {
        setRecommendations('Whole Life', 'Term');
        return;
    }
    // row 26
    if (
        evaluateRules({
            focusCategories: ['INHERITANCE'],
            age: 'AGE_20_50',
            income: 'HHI_GT35',
            fundingPlan: ['FUND_MONTHLY', 'FUND_BOTH'],
        })
    ) {
        setRecommendations('IUL', 'Whole Life');
        return;
    }
    // row 27
    if (
        evaluateRules({
            focusCategories: ['INHERITANCE'],
            age: 'AGE_50_PLUS',
            income: 'HHI_LTE35',
            fundingPlan: ['FUND_LUMP'],
        })
    ) {
        setRecommendations('Whole Life', 'Term');
        return;
    }
    // row 28
    if (
        evaluateRules({
            focusCategories: ['INHERITANCE'],
            age: 'AGE_50_PLUS',
            income: 'HHI_GT35',
            fundingPlan: ['FUND_LUMP'],
        })
    ) {
        setRecommendations('IUL', 'Whole Life');
        return;
    }
    // row 29
    if (
        evaluateRules({
            focusCategories: ['INHERITANCE'],
            age: 'AGE_50_PLUS',
            income: 'HHI_LTE35',
            fundingPlan: ['FUND_MONTHLY', 'FUND_BOTH'],
        })
    ) {
        setRecommendations('Whole Life', 'Term');
        return;
    }
    // row 30
    if (
        evaluateRules({
            focusCategories: ['INHERITANCE'],
            age: 'AGE_50_PLUS',
            income: 'HHI_GT35',
            fundingPlan: ['FUND_MONTHLY', 'FUND_BOTH'],
        })
    ) {
        setRecommendations('IUL', 'Whole Life');
        return;
    }

    // Default fallback if no rules match
    setRecommendations('IUL', 'Term', 'Default recommendation. Please notify management to review this case.');
};
